import { useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import type { AlphaMask } from '../render/alphaMask';
import type { RoomSceneState } from './roomSceneState';

// ── Alpha-mask decode cache (per-image, promise-deduped) ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass).
// `sceneRef.current.caches.alphaMask` stays owned by RoomCanvas (it is also passed to useRoomScene
// for teardown) and is received as a parameter; `alphaMaskLoadingRef` (in-flight
// promise dedupe map) had this cluster as its sole consumer and moved in with it.

/** The alpha channel of a picture (1 byte per pixel), or null if it cannot be read. */
function alphaMaskOf(image: CanvasImageSource, width: number, height: number): AlphaMask | null {
  try {
    if (!width || !height) return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(image, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    // Extract only alpha channel (1 byte/pixel instead of 4) to save ~75% memory
    const alphaOnly = new Uint8Array(width * height);
    for (let i = 0; i < width * height; i++) {
      alphaOnly[i] = imageData.data[i * 4 + 3];
    }
    return { width, height, data: alphaOnly };
  } catch {
    return null;
  }
}

export interface UseAlphaMaskCacheParams {
  sceneRef: React.MutableRefObject<RoomSceneState>;
}

export function useAlphaMaskCache({ sceneRef }: UseAlphaMaskCacheParams): {
  ensureAlphaMaskLoaded: (imagePath: string) => Promise<AlphaMask | null>;
  peekAlphaMask: (imagePath: string) => AlphaMask | null | undefined;
} {
  const alphaMaskLoadingRef = useRef<Map<string, Promise<AlphaMask | null>>>(new Map());

  const ensureAlphaMaskLoaded = useCallback(async (imagePath: string): Promise<AlphaMask | null> => {
    const cached = sceneRef.current.caches.alphaMask.get(imagePath);
    if (cached !== undefined) return cached;

    const inFlight = alphaMaskLoadingRef.current.get(imagePath);
    if (inFlight) return inFlight;

    const loader = new Promise<AlphaMask | null>((resolve) => {
      const done = (mask: AlphaMask | null) => {
        sceneRef.current.caches.alphaMask.set(imagePath, mask);
        resolve(mask);
      };
      // The furniture texture is usually decoded already (PIXI loaded it, often
      // while the splash was up): read its pixels instead of decoding the file again.
      const decoded = (PIXI.Assets.get(imagePath) as PIXI.Texture | undefined)?.source?.resource as CanvasImageSource | undefined;
      const size = decoded as { width?: number; height?: number } | undefined;
      if (decoded && size?.width && size?.height) {
        done(alphaMaskOf(decoded, size.width, size.height));
        return;
      }
      const img = new Image();
      img.decoding = 'async';
      img.onload = () => done(alphaMaskOf(img, img.naturalWidth || img.width, img.naturalHeight || img.height));
      img.onerror = () => done(null);
      img.src = imagePath;
    }).finally(() => {
      alphaMaskLoadingRef.current.delete(imagePath);
    });

    alphaMaskLoadingRef.current.set(imagePath, loader);
    return loader;
  }, [sceneRef]);

  // Synchronous cache peek — returns the mask only if already decoded (re-entry /
  // duplicate furniture). `undefined` means "not loaded yet"; `null` means "loaded
  // but no usable mask". Lets the render loop apply masks instantly without an await
  // when they're already in cache (no visual settle on re-entry).
  const peekAlphaMask = useCallback((imagePath: string): AlphaMask | null | undefined => {
    return sceneRef.current.caches.alphaMask.get(imagePath);
  }, [sceneRef]);

  return { ensureAlphaMaskLoaded, peekAlphaMask };
}
